<script lang="ts" setup>
import * as dateFns from 'date-fns'
import { useToast } from 'primevue/usetoast'
import { formatDate, formatDates, formatList, matomo } from '@/lib/helpers'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import { RouterLink } from 'vue-router'

// const gridItemImageRefs = ref<HTMLElement[]>([])

const filterHeaderIntersectionWrapper = ref<HTMLElement>()
const scrolled = ref(false)

const optionsStore = useOptionsStore()
const lifecycleStore = useLifecycleStore()

const head = computed(() => ({
  title: `${optionsStore.pageTitle} | ${optionsStore.siteTitle}`,
}))
useHead(head)

watch(
  () => head.value.title,
  title => {
    if (!title) return
    matomo('setDocumentTitle', title)
  },
)

onMounted(() => {
  lifecycleStore.initialized = true

  filterHeaderIntersectionWrapper.value!.style.setProperty('width', 'var(--scroll-offset)')
  const scrollOffset = getComputedStyle(filterHeaderIntersectionWrapper.value!).getPropertyValue(
    'width',
  )
  filterHeaderIntersectionWrapper.value!.style.removeProperty('width')

  useIntersectionObserver(
    filterHeaderIntersectionWrapper,
    ([entry]) => {
      scrolled.value = !entry.isIntersecting
    },
    {
      rootMargin: `-${scrollOffset || '0px'} 0px 0px 0px`,
    },
  )
})

const toast = useToast()

function errorToast() {
  toast.add({
    severity: 'error',
    summary: 'Fehler beim Laden',
    detail: 'Daten konnten nicht geladen werden.',
    life: 3000,
  })
}

const sortOptions = ref([
  { label: 'Name', value: 'family-name' },
  { label: 'Vorname', value: 'first-name' },
  { label: 'Geburtsdatum', value: 'date-of-birth' },
  { label: 'Sterbedatum', value: 'date-of-death' },
])

const viewOptions = ref([
  { label: 'Listenansicht', value: 'list', icon: 'pi pi-list' },
  { label: 'Kachelansicht', value: 'grid', icon: 'pi pi-microsoft' },
])

const filterResultsStore = useFilterResultsStore()
const filterInputStore = useFilterInputStore()

const sortingLabel = computed(
  () => sortOptions.value.find(item => item.value === filterInputStore.values.sorting)!.label,
)

const viewLabel = computed(
  () => viewOptions.value.find(item => item.value === filterInputStore.values.view)!.label,
)

const hasPermanentSidebar = useMediaQuery('(width > 800px)')
const showSidebar = ref(false)

const liveSearchQuery = ref('')
watch(
  () => filterInputStore.values.search,
  value => {
    liveSearchQuery.value = value
  },
  { immediate: true },
)

const encodedQuery = computed(() => encodeURIComponent(filterResultsStore.query))

const placeholderImage = import.meta.env.BASE_URL + '/placeholder-person.svg'
</script>

<template>
  <div class="person-filter-container">
    <div class="person-filter">
      <div
        class="sidebar-container"
        :class="{ show: showSidebar }"
        :inert="!hasPermanentSidebar && !showSidebar"
      >
        <aside class="sidebar">
          <header class="sidebar-header">
            <div class="sidebar-header-apply">
              <!--              <ProgressSpinner-->
              <!--                v-if="filterResultsStore.isFetching"-->
              <!--                style="width: 24px; height: 24px"-->
              <!--                strokeWidth="4"-->
              <!--                animationDuration=".5s"-->
              <!--              />-->
              <!--              <div class="filter-icon" v-else />-->
              <span class="primary-line-background">Suche</span>
            </div>
            <div class="sidebar-header-hide" @click="showSidebar = false">
              <Button icon="pi pi-times" text aria-label="Filter schliessen" />
            </div>
          </header>
          <div class="sidebar-content">
            <div class="search-field-container">
              <IconField class="p-icon-field p-icon-field-left">
                <div class="p-input-icon" style="transform: translateY(-0.275em)">
                  <!-- prettier-ignore -->
                  <svg xmlns="http://www.w3.org/2000/svg" class="16" height="16" viewBox="0 0 57 57"
                       xml:space="preserve"><path d="M55.1 51.9 41.6 37.8A23 23 0 0 0 24 0a23 23 0 1 0 13.2 41.8L50.8 56a3 3 0 0 0 4.3.1 3 3 0 0 0 0-4.2zM24 6a17 17 0 1 1 0 34 17 17 0 0 1 0-34z" fill="currentColor"/></svg>
                </div>
                <InputText
                  type="search"
                  class="search-field"
                  placeholder="Suche"
                  v-model.lazy="liveSearchQuery"
                  @change="filterInputStore.values.search = liveSearchQuery"
                />
              </IconField>
            </div>
            <PersonalDataFilter />
            <PositionFilter />
            <OccupationFilter />
            <GNDFilter />
            <div v-if="filterInputStore.isActive">
              <Button
                icon="pi pi-times"
                text
                label="Filter zurücksetzen"
                @click="filterInputStore.reset()"
              />
            </div>
          </div>
        </aside>
      </div>
      <main :class="{ loading: filterResultsStore.isFetching }">
        <div v-if="filterResultsStore.isFetching" class="loading-info">
          <div class="message">
            <ProgressSpinner
              v-if="filterResultsStore.isFetching"
              style="width: 42px; height: 42px"
              strokeWidth="4"
              animationDuration=".5s"
            />
            durchsuche die lokale Datenbank nach passenden Personen
          </div>
        </div>
        <div ref="filterHeaderIntersectionWrapper" class="filter-header-intersection-wrapper"></div>
        <header class="filter-header" :class="{ scrolled }">
          <span class="primary-line-background">Resultate</span>
          <Button
            class="filter-header-toggle"
            icon="pi pi-filter"
            text
            aria-label="Filter öffnen"
            @click="showSidebar = !showSidebar"
          />

          <div class="filter-header-actions">
            <Dropdown
              v-model="filterInputStore.values.sorting"
              placeholder="Sortieren nach"
              :options="sortOptions"
              optionLabel="label"
              optionValue="value"
            >
              <template #value>
                <div class="dropdown-content">
                  <i class="pi pi-sort-alpha-down"></i>
                  <span class="dropdown-label">{{ sortingLabel }}</span>
                </div>
              </template>
            </Dropdown>

            <!--          <SelectButton-->
            <!--            class="view-select"-->
            <!--            v-model="filterInputStore.values.view"-->
            <!--            :options="viewOptions"-->
            <!--            option-value="value"-->
            <!--            option-label="label"-->
            <!--            :allow-empty="false"-->
            <!--          >-->
            <!--            <template #option="scope">-->
            <!--              <div class="view-select-content">-->
            <!--                <i :class="['view-icon', scope.option.icon]"></i>-->
            <!--                <span class="view-label">{{ scope.option.label }}</span>-->
            <!--              </div>-->
            <!--            </template>-->
            <!--          </SelectButton>-->

            <Dropdown
              v-model="filterInputStore.values.view"
              placeholder="Sortieren nach"
              :options="viewOptions"
              optionLabel="label"
              optionValue="value"
            >
              <template #value="slotProps">
                <div class="dropdown-content">
                  <i class="pi pi-list"></i>
                  <div class="dropdown-label">{{ viewLabel }}</div>
                </div>
              </template>
            </Dropdown>
          </div>
        </header>
        <div class="results-container">
          <div v-if="!filterResultsStore.isFetching">
            <!-- An error has occurred -->
            <template v-if="filterResultsStore.error">
              <Message severity="error" :closable="false"
                >{{ filterResultsStore.error }}
              </Message>
            </template>

            <!-- No error happened, but result set is empty -->
            <template v-else-if="filterResultsStore.data.length === 0">
              <Message severity="secondary" :closable="false">Keine Ergebnisse gefunden.</Message>
            </template>
          </div>

          <!-- No errors have been surfaced -->
          <template v-if="!filterResultsStore.error">
            <div class="results grid" v-if="filterInputStore.values.view === 'grid'">
              <template
                v-for="person in filterResultsStore.data"
                :key="`results-list-item-${person.id}`"
              >
                <RouterLink :to="`/person/${person.id}`" class="grid-item">
                  <CroppedImage
                    :source="person.image ?? placeholderImage"
                    :lazy="Boolean(person.image)"
                  />
                  <div>
                    <strong>{{ person.label }}</strong
                    ><br />
                    <template
                      v-if="person.datesOfBirth.length > 0 || person.datesOfDeath.length > 0"
                    >
                      <template v-if="person.datesOfDeath.length === 0">
                        *
                        {{ formatDates(person.datesOfBirth) }}
                      </template>
                      <template v-else-if="person.datesOfBirth.length === 0">
                        †
                        {{ formatList(formatDates(person.datesOfDeath)) }}
                      </template>
                      <template v-else>
                        <span
                          >{{ formatDates(person.datesOfBirth) }} –
                          {{ formatDates(person.datesOfDeath) }}</span
                        >
                      </template>
                      <br />
                    </template>
                  </div>
                  <span>{{ formatList(person.occupations, 'conjunction') }}</span>
                </RouterLink>
              </template>
            </div>
            <div class="results list" v-if="filterInputStore.values.view === 'list'">
              <template v-for="person in filterResultsStore.data">
                <RouterLink :to="`/person/${person.id}`" class="list-item">
                  <div class="image">
                    <CroppedImage
                      :source="person.image ?? placeholderImage"
                      :lazy="Boolean(person.image)"
                      mode="cover"
                    />
                  </div>
                  <div class="name">{{ person.label }}</div>
                  <div class="lifespan">
                    <template
                      v-if="person.datesOfBirth.length > 0 || person.datesOfDeath.length > 0"
                    >
                      <template v-if="person.datesOfDeath.length === 0">
                        *
                        {{ formatDates(person.datesOfBirth) }}
                      </template>
                      <template v-else-if="person.datesOfBirth.length === 0">
                        †
                        {{ formatList(formatDates(person.datesOfDeath)) }}
                      </template>
                      <template v-else>
                        <span
                          >{{ formatDates(person.datesOfBirth) }} –
                          {{ formatDates(person.datesOfDeath) }}</span
                        >
                      </template>
                      <br />
                    </template>
                  </div>
                  <div class="positions">{{ formatList(person.occupations, 'conjunction') }}</div>
                </RouterLink>
              </template>
            </div>
          </template>

          <Button
            v-if="filterResultsStore.hasMore"
            class="load-more-button"
            type="button"
            label="Mehr Ergebnisse anzeigen"
            severity="secondary"
            :disabled="filterResultsStore.isFetching"
            @click="filterResultsStore.loadMore()"
          />
        </div>
      </main>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@mixin with-permanent-sidebar {
  @media (width > 800px) {
    @content;
  }
}

.person-filter-container {
  container: filter / inline-size;
}

.person-filter {
  /*--border-style: 1px solid #cbd5e1;*/
  --border-style: 2px solid #f2f2f2;
  --box-shadow: 0 6px 0px -4px #e3e3e380;
  --light-text-color: #ababab;

  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 45px;

  @include with-permanent-sidebar {
    grid-template-columns: auto 1fr;
  }
}

.search-field-container {
  padding-top: 1.25rem;
  padding-bottom: 0.75rem;

  input {
    padding-top: 1em;
    padding-bottom: 1em;
  }
}

.search-field {
  width: 100%;
}

.sidebar-container {
  height: 100%;
  width: calc(min(350px, 100% - 2rem) + var(--container-padding));
  margin-left: calc(-1 * var(--container-padding));
  grid-row: 1 / -1;
  grid-column: 1 / -1;
  transform: translateX(-100%) translateX(-6px);
  transition: transform 300ms;
  z-index: 5;

  &:is(.show) {
    transform: none;
  }

  @include with-permanent-sidebar {
    grid-row: initial;
    grid-column: initial;
    transform: none;
    position: static;
    width: auto;
    height: auto;
    margin-left: initial;
  }
}

aside.sidebar {
  --sidebar-gap: 1rem;
  --sidebar-vertical-margin: calc(1 * var(--sidebar-gap));
  --sidebar-offset: 1rem;

  background-color: #fff;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-gap: var(--sidebar-gap);
  width: 100%;
  // height: calc(100dvh - var(--header-height));
  position: sticky;
  top: var(--scroll-offset, 0px);
  box-shadow: 6px 0 5px -5px rgb(0 0 0 / 15%);
  padding-top: var(--sidebar-offset);
  padding-bottom: 1rem;
  padding-right: 1rem;
  padding-left: var(--container-padding);

  @include with-permanent-sidebar {
    --sidebar-gap: 35px;
    top: var(--scroll-offset-padded);
    width: 300px;
    height: calc(
      100dvh - var(--sidebar-offset) - var(--scroll-offset-padded) - var(--sidebar-vertical-margin)
    );
    padding-top: 0;
    padding-bottom: 0;
    padding-right: 0;
    padding-left: 0;
    box-shadow: none;
  }

  .sidebar-header {
    color: #000;
    display: flex;
    justify-content: space-between;

    .filter-icon {
      --width: 24px;
      --height: 22px;
      width: var(--width);
      height: var(--height);
      content: '';
      background-color: currentColor;
      display: inline-flex;
      mask-image: url('@/assets/img/filter.svg');
      mask-size: cover;
      mask-repeat: no-repeat;
    }

    .sidebar-header-apply {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .sidebar-header-hide {
      display: block;

      @include with-permanent-sidebar {
        display: none;
      }
    }
  }

  .sidebar-content {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1em;
  }
}

main {
  --grid-gap: 25px;

  grid-row: 1 / -1;
  grid-column: 1 / -1;
  position: relative;
  display: grid;
  grid-gap: var(--grid-gap);
  grid-template-rows: auto 1fr;
  min-width: 0;

  @include with-permanent-sidebar {
    grid-row: initial;
    grid-column: initial;
  }

  &.loading .results {
    opacity: 0.5;
  }

  .filter-header-intersection-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    z-index: -1;
    pointer-events: none;
  }

  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    //position: sticky;
    top: calc(var(--scroll-offset, 0px) - 1px);
    z-index: 1;
    background: #fff;
    padding-top: calc(0.5rem + 1px);
    padding-bottom: 0.5rem;
    margin-block: -0.5rem;
    transition: box-shadow 300ms;
    padding-inline: var(--container-padding);
    margin-inline: calc(-1 * var(--container-padding));

    &.scrolled {
      box-shadow: 0 1px 2px #0000001a;
    }

    @include with-permanent-sidebar {
      margin-inline: 0;
      padding-inline: 0;

      &.scrolled {
        box-shadow: 0 2px 2px -1px #0000001a;
      }
    }

    .dropdown-content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.25rem;

      .dropdown-label {
        display: none;
      }

      @container filter (width > 525px) {
        margin-top: 0;

        .dropdown-label {
          display: block;
        }
      }
    }
  }

  .filter-header-toggle {
    @include with-permanent-sidebar {
      display: none;
    }
  }

  .filter-header-actions {
    display: flex;
    gap: 1ch;
  }

  .results-container {
    container: results / inline-size;
  }

  .results {
    transition: opacity ease 200ms;

    &.grid {
      --columns: 2;
      display: grid;
      grid-template-columns: repeat(var(--columns), 1fr);
      grid-gap: var(--grid-gap);

      @container results (width > 600px) {
        --columns: 3;
      }
    }

    &.list {
      display: grid;
      grid-template-columns: 1fr;
      //grid-gap: calc(var(--grid-gap) * 0.5);

      .list-item {
        display: grid;
        grid-template-columns: 70px 1fr;
        grid-template-rows: auto auto 1fr;
        grid-template-areas: 'avatar name' 'avatar lifespan' 'avatar positions';
        column-gap: 1em;
        border-top: 1px solid #dadada;
        padding: 0.5em 0;
        color: inherit;

        @container results (width > 450px) {
          grid-template-columns: 70px 1fr 1fr;
          grid-template-rows: auto auto;
          grid-template-areas: 'avatar name positions' 'avatar lifespan positions';
        }

        .image {
          grid-area: avatar;
        }

        .name {
          font-weight: 600;
          grid-area: name;
        }

        .lifespan {
          grid-area: lifespan;
        }

        .positions {
          grid-area: positions;
        }

        &:last-child {
          border-bottom: 2px solid #efefef;
        }

        img {
          --size: 70px;
          aspect-ratio: 1/1;
          object-fit: cover;
          object-position: top;
          width: var(--size);
          align-self: center;
        }
      }
    }

    .grid-item {
      color: inherit;
      background-color: #fff;
    }
  }

  .loading-info {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: flex-start;

    .message {
      position: sticky;
      top: 50%;
      margin: 4em 0;
      display: flex;
      align-items: center;
      gap: 1rem;
      font-size: 1rem;
      line-height: 1.1;
      font-weight: 500;
      background-color: #000c;
      backdrop-filter: blur(5px);
      color: #fff;
      border-radius: 50px;
      padding: 1rem 1.25rem 1rem 1.25rem;
      width: 300px;
    }
  }
}

.view-select {
  height: 100%;
}

.view-select-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 100%;

  .view-icon {
    display: flex;
    align-items: center;
    min-height: 1.5em;
    height: 100%;
  }

  .view-label {
    z-index: 1;
    display: none;

    @container filter (width > 600px) {
      display: block;
    }
  }
}

.load-more-button {
  display: block;
  margin-inline: auto;
  margin-block: 3rem;
}
</style>
